"use client"
import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { CtaBanner } from "@/components/ui/cta-banner"
import { useWaitlist } from "@/components/ui/waitlist-context"
import { ValuePropCards } from "@/components/ui/value-prop-cards"
import { useTranslations } from "@/lib/intl/use-translations"
import { blobMediaUrl } from "@/lib/blob-url"
import dynamic from "next/dynamic"
import { AUSTRIA_COORDS } from "@/data/austria-border"

const LocationMap = dynamic(
  () => import("@/components/ui/location-map").then((m) => m.LocationMap),
  {
    ssr: false,
    // Full-bleed skeleton matches the loaded tile tint so there's no
    // white→green pop when Leaflet's first tiles render (mirrors CH/DE).
    loading: () => (
      <div
        className="h-full w-full"
        style={{ background: "var(--color-sand-100)" }}
      />
    ),
  }
)

const VALUE_PROP_KEYS = [
  "valueProp1",
  "valueProp2",
  "valueProp3",
  "valueProp4",
] as const

const AT_CITIES = [
  { id: "wien", nameKey: "cityWien", lat: 48.2082, lng: 16.3738 },
  { id: "graz", nameKey: "cityGraz", lat: 47.0707, lng: 15.4395 },
  { id: "linz", nameKey: "cityLinz", lat: 48.3069, lng: 14.2858 },
  { id: "salzburg", nameKey: "citySalzburg", lat: 47.8095, lng: 13.055 },
  { id: "innsbruck", nameKey: "cityInnsbruck", lat: 47.2692, lng: 11.4041 },
] as const

export default function AtLocationsPage() {
  const t = useTranslations("locationsAt")
  const th = useTranslations("home")
  const { openWaitlist } = useWaitlist()

  const cities = AT_CITIES.map((c) => ({
    id: c.id,
    label: t(c.nameKey),
    lat: c.lat,
    lng: c.lng,
  }))

  // Push the map's bounds-fit below the overlaid hero text so the pins are
  // never hidden behind it — measured live, like the CH/DE locations hero.
  const heroContentRef = useRef<HTMLDivElement>(null)
  const [mapPadTop, setMapPadTop] = useState(340)

  useEffect(() => {
    const el = heroContentRef.current
    if (!el) return
    const update = () => {
      const section = el.parentElement
      if (!section) return
      const bottomFromSectionTop =
        el.getBoundingClientRect().bottom - section.getBoundingClientRect().top
      // +56 keeps even the northernmost pin (Linz) clear of the CTA
      // button, so pins stay clickable and aren't hidden behind the overlay.
      setMapPadTop(Math.ceil(bottomFromSectionTop + 56))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    window.addEventListener("resize", update)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", update)
    }
  }, [])

  return (
    <>
      {/* ─── Hero: full-bleed map with overlaid text (mirrors CH/DE) ─── */}
      {/* Map tile color adjustment — scoped to the locations hero. */}
      <style>{`.locations-hero-map .leaflet-tile-pane img { filter: saturate(0.48) !important; }`}</style>
      <section className="relative h-[calc(100dvh-108px)] overflow-hidden">
        {/* Layer 0: full-bleed map. Pins open the waitlist pre-filled. */}
        <div className="locations-hero-map absolute inset-0 z-0">
          <LocationMap
            clinics={[]}
            comingSoonCities={cities}
            onCityClick={(city) => openWaitlist(city)}
            fullBleed
            mapPadTop={mapPadTop}
            mapPad={0.04}
            tileTint={{ color: "#4fa176", opacity: 0.3 }} // ds-color-ignore: translucent overlay
            borderData={AUSTRIA_COORDS}
            centerCoords={[47.7, 13.3]}
            defaultZoom={7}
          />
        </div>

        {/* Layer 1: top gradient for text readability over the map. */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-[340px]"
          style={{
            background:
              "linear-gradient(to bottom, #f4f4f4 30%, transparent 100%)",
          }}
        />

        {/* Layer 2: text content over the map. */}
        <div
          ref={heroContentRef}
          className="pointer-events-none relative z-[2] px-4 pt-8 text-center md:px-10 md:pt-10"
        >
          <div className="mx-auto max-w-2xl">
            <p className="text-aubergine-500 pointer-events-auto mb-4 text-sm font-medium tracking-wider uppercase">
              {t("comingSoon")}
            </p>
            <h1 className="text-aubergine-900 pointer-events-auto text-3xl font-normal sm:text-5xl md:text-6xl">
              {t("heading")}
            </h1>
            <p className="pointer-events-auto mx-auto mt-4 max-w-2xl text-lg text-neutral-500">
              {t("subtitle")}
            </p>

            {/* Primary CTA */}
            <div className="mt-8">
              <Button
                onClick={() => openWaitlist()}
                className="bg-aubergine-800 hover:bg-aubergine-900 pointer-events-auto px-8"
              >
                {t("waitlistCta")}
              </Button>
            </div>

            {/* "Other city" CTA — for people outside the 5 target cities */}
            <p className="pointer-events-auto mt-5 text-sm text-neutral-400">
              {t("otherCityPrompt")}{" "}
              <button
                type="button"
                onClick={() => openWaitlist()}
                className="text-aubergine-600 hover:text-aubergine-800 cursor-pointer underline underline-offset-2 transition-colors"
              >
                {t("otherCityCta")}
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* No social-proof carousel on AT: there are no Austrian testimonials
          yet, and its heading read "In der Schweiz bereits etabliert". */}
      {/* ─── Value props: 4 colored cards ─── */}
      <ValuePropCards labels={VALUE_PROP_KEYS.map((key) => th(key))} />

      {/* ─── Bottom CTA ─── */}
      <div>
        <CtaBanner
          imageSrc={blobMediaUrl("/images/homepage/cta-bottom.jpeg")}
          title={t("ctaTitle")}
          subtitle={t("ctaSubtitle")}
          primaryCta={{
            label: t("waitlistCta"),
            onClick: () => openWaitlist(),
          }}
        />
      </div>
    </>
  )
}
